/**
 * Toast bridge — preserves the existing `useToast()` API but delegates to
 * Sonner. All call sites (useToast().show/success/error) keep working without
 * modification.
 */
import { Toaster, toast as sonner } from 'sonner';
import type { ReactNode } from 'react';

type ToastKind = 'success' | 'error' | 'info';

interface ToastApi {
  show(message: string, kind?: ToastKind): void;
  success(message: string): void;
  error(message: string): void;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster
        position="top-right"
        theme="light"
        richColors
        closeButton
        toastOptions={{
          classNames: {
            toast:
              'rounded-xl border border-zinc-200 bg-white shadow-soft font-sans text-[13px] tracking-tightish',
            title: 'font-medium text-zinc-900',
            description: 'text-zinc-500',
          },
        }}
      />
    </>
  );
}

const api: ToastApi = {
  show(message, kind = 'info') {
    if (kind === 'success') sonner.success(message);
    else if (kind === 'error') sonner.error(message);
    else sonner.message(message);
  },
  success(message) {
    sonner.success(message);
  },
  error(message) {
    sonner.error(message, { duration: 7000 });
  },
};

export function useToast(): ToastApi {
  return api;
}

export const toast = api;
